/**
 * Entry fees: what is in, what is out, and who still owes.
 *
 * The GET already resolves each entry's status as COALESCE(ledger, flag) and
 * returns the totals, so nothing is summed here — money added up in two places
 * is money that eventually disagrees with itself.
 */

import { useState } from 'react';
import { apiPut } from '../../lib/api.js';
import PaymentToggle from './PaymentToggle.jsx';

const money = (n) => (typeof n === 'number' ? n.toFixed(2) : '—');

export default function PaymentsTab({ venueId, tournamentId, payments, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError]   = useState(null);

  if (!payments) return <div className="hd-empty">Loading payments…</div>;

  const { entries, totals } = payments;

  const setPaid = async (entry, status) => {
    setBusyId(entry.entryId);
    setError(null);

    const { error: err } = await apiPut(
      `/api/venues/${venueId}/tournaments/${tournamentId}/payments`,
      { entryId: entry.entryId, status }
    );

    setBusyId(null);
    if (err) { setError(err); return; }
    await onChanged();
  };

  return (
    <div className="hd-stack">
      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-tiles">
        <div className="hd-tile">
          <span className="hd-tile-label">Collected</span>
          <span className="hd-tile-value">£{money(totals.amountCollected)}</span>
          <span className="hd-meta">{totals.totalPaid} of {totals.totalEntries} paid</span>
        </div>
        <div className="hd-tile">
          <span className="hd-tile-label">Outstanding</span>
          <span className="hd-tile-value">£{money(totals.amountOutstanding)}</span>
          <span className="hd-meta">{totals.totalUnpaid} still to pay</span>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="hd-empty">No entries to bill.</div>
      ) : (
        <div className="hd-table-wrap">
          <table className="hd-table hd-admin-table">
            <thead>
              <tr>
                <th>Player</th>
                <th className="hd-table-num">Amount</th>
                <th>Status</th>
                <th className="hd-table-right">Paid</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.entryId}>
                  <td>
                    {entry.displayName}
                    {entry.isGuest && <span className="hd-guest-tag">guest</span>}
                  </td>
                  {/* The ledger's amount, not the tournament's current fee: a
                      fee changed later must not restate what somebody already
                      handed over. */}
                  <td className="hd-table-num">£{money(entry.amount)}</td>
                  <td>
                    <span className={`hd-badge hd-badge-${entry.status === 'paid' ? 'success' : 'pending'}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="hd-table-right">
                    <PaymentToggle
                      paid={entry.status === 'paid'}
                      busy={busyId === entry.entryId}
                      onChange={(status) => setPaid(entry, status)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
